// src/pages/seasons/DocumentUploadPage.jsx
// Public page — no login required
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, app } from '@/firebase/firebase';
import { ArrowPathIcon, CheckCircleIcon, DocumentArrowUpIcon, PhotoIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { setAppLanguage } from '@/i18n';

const DOC_TYPES = {
  birth_certificate: {
    label: 'Birth Certificate / Passport',
    labelEs: 'Acta de Nacimiento / Pasaporte',
    accept: 'image/*,application/pdf',
    icon: '🪪',
    hint: 'Photo of birth certificate or passport — JPG, PNG, or PDF',
    hintEs: 'Foto del acta de nacimiento o pasaporte — JPG, PNG o PDF',
  },
  player_photo: {
    label: 'Player Photo',
    labelEs: 'Foto del Jugador',
    accept: 'image/*',
    capture: 'user',
    icon: '📸',
    hint: 'Clear headshot photo of the player',
    hintEs: 'Foto de perfil clara del jugador',
  },
  club_registration: {
    label: 'Club / League Registration Form',
    labelEs: 'Formulario de Registro del Club',
    accept: 'image/*,application/pdf',
    icon: '📄',
    hint: 'US Soccer R002, US Club, or similar registration document',
    hintEs: 'US Soccer R002, US Club u otro formulario de registro',
  },
  medical_form: {
    label: 'Medical / Physical Form',
    labelEs: 'Formulario Médico',
    accept: 'image/*,application/pdf',
    icon: '🩺',
    hint: 'Completed physical exam or medical clearance form',
    hintEs: 'Formulario de examen físico o autorización médica',
  },
  insurance_card: {
    label: 'Insurance Card',
    labelEs: 'Tarjeta de Seguro',
    accept: 'image/*,application/pdf',
    icon: '🏥',
    hint: 'Front of health insurance card',
    hintEs: 'Frente de la tarjeta de seguro médico',
  },
};

export default function DocumentUploadPage() {
  const { teamId, registrationId } = useParams();
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const setLang = (next) => setAppLanguage(typeof next === 'function' ? next(lang) : next);
  const [status, setStatus] = useState('loading'); // loading | open | done | error
  const [registration, setRegistration] = useState(null);
  const [season, setSeason] = useState(null);
  const [team, setTeam] = useState(null);
  const [uploads, setUploads] = useState({}); // { [docType]: { status: 'idle'|'uploading'|'done'|'error', progress: 0-100, url, fileName } }

  /* ── Load data ─────────────────────────────────── */
  useEffect(() => {
    async function load() {
      try {
        const regSnap = await getDoc(doc(db, 'registrations', registrationId));
        if (!regSnap.exists()) { setStatus('error'); return; }
        const reg = { id: regSnap.id, ...regSnap.data() };
        setRegistration(reg);

        // Load season or program context
        if (reg.seasonId) {
          // Season registration
          const [seasonSnap, teamSnap] = await Promise.all([
            getDoc(doc(db, 'seasons', reg.seasonId)),
            (reg.teamId || teamId) ? getDoc(doc(db, 'teams', reg.teamId || teamId)) : Promise.resolve({ exists: () => false }),
          ]);
          if (seasonSnap.exists()) setSeason({ id: seasonSnap.id, ...seasonSnap.data() });
          if (teamSnap.exists()) setTeam({ id: teamSnap.id, ...teamSnap.data() });
        } else if (reg.programId) {
          // Program registration — load program as season context, team separately
          const fetches = [getDoc(doc(db, 'programs', reg.programId))];
          if (reg.teamId) fetches.push(getDoc(doc(db, 'teams', reg.teamId)));
          const [progSnap, teamSnap] = await Promise.all(fetches);
          if (progSnap.exists()) {
            const progData = progSnap.data();
            // Normalize: store program as "season" state so the rest of the component works unchanged
            // Map requiredDocTypes → requiredDocuments for compatibility
            setSeason({
              id: progSnap.id,
              name: progData.name,
              requiredDocuments: progData.requiredDocTypes || [],
              _isProgram: true,
            });
          }
          if (teamSnap?.exists()) setTeam({ id: teamSnap.id, ...teamSnap.data() });
        }

        // Pre-populate already-uploaded docs
        if (reg.documents) {
          const existing = {};
          Object.entries(reg.documents).forEach(([type, data]) => {
            existing[type] = { status: 'done', url: data.url, fileName: data.fileName || 'Uploaded' };
          });
          setUploads(existing);
        }

        setStatus('open');
      } catch (err) {
        console.error('[DocumentUploadPage]', err);
        setStatus('error');
      }
    }
    if (registrationId) load();
  }, [registrationId, teamId]);

  /* ── Upload handler ─────────────────────────────── */
  const handleFileSelect = async (docType, file) => {
    if (!file) return;
    const storage = getStorage(app);
    const ext = file.name.split('.').pop();
    const path = `registrations/${registrationId}/docs/${docType}.${ext}`;
    const sRef = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(sRef, file);

    setUploads(prev => ({ ...prev, [docType]: { status: 'uploading', progress: 0, fileName: file.name } }));

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploads(prev => ({ ...prev, [docType]: { ...prev[docType], progress } }));
      },
      (err) => {
        console.error('[upload]', err);
        setUploads(prev => ({ ...prev, [docType]: { status: 'error', progress: 0, fileName: file.name } }));
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setUploads(prev => ({ ...prev, [docType]: { status: 'done', progress: 100, url, fileName: file.name } }));
        // Save to Firestore
        await updateDoc(doc(db, 'registrations', registrationId), {
          [`documents.${docType}`]: { url, fileName: file.name, uploadedAt: serverTimestamp() },
          updatedAt: serverTimestamp(),
        });
      }
    );
  };

  /* ── Status screens ─────────────────────────────── */
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-100 shadow-lg p-8 text-center">
          <div className="text-5xl mb-4">❌</div>
          <h1 className="text-xl font-bold text-slate-900">Link Not Found</h1>
          <p className="mt-2 text-sm text-slate-500">This upload link is invalid or has expired. Contact your coach for a new link.</p>
        </div>
      </div>
    );
  }

  const requiredDocs = season?.requiredDocuments || [];
  const uploadedCount = requiredDocs.filter(d => uploads[d]?.status === 'done').length;
  const allDone = requiredDocs.length > 0 && uploadedCount === requiredDocs.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Language toggle */}
      <div className="flex justify-end px-4 pt-4 max-w-xl mx-auto">
        <button
          onClick={() => setLang(l => l === 'en' ? 'es' : 'en')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600 shadow-sm hover:border-blue-300 hover:text-blue-600 transition-colors"
        >
          <span>{lang === 'en' ? '🇲🇽' : '🇺🇸'}</span>
          {lang === 'en' ? 'Español' : 'English'}
        </button>
      </div>

      {/* Header */}
      <div className="pt-5 pb-6 px-4 max-w-xl mx-auto text-center">
        {team?.logoUrl ? (
          <img src={team.logoUrl} alt={team.name} className="h-14 w-14 rounded-2xl object-cover border border-slate-100 shadow-md mx-auto mb-3" />
        ) : (
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-3 shadow-md">
            <span className="text-xl font-black text-white">{team?.name?.[0] ?? '⚽'}</span>
          </div>
        )}
        <h1 className="text-xl font-extrabold text-slate-900">{team?.name}</h1>
        <p className="text-blue-600 font-semibold text-sm mt-0.5">{season?.name}</p>
        <p className="text-slate-500 text-sm mt-2">
          {lang === 'en'
            ? `Documents for ${registration?.playerName || 'your player'}`
            : `Documentos para ${registration?.playerName || 'su jugador'}`}
        </p>

        {/* Progress */}
        {requiredDocs.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(uploadedCount / requiredDocs.length) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-500">{uploadedCount}/{requiredDocs.length}</span>
          </div>
        )}
      </div>

      <div className="max-w-xl mx-auto px-4 pb-16 space-y-4">

        {requiredDocs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-sm text-slate-500">
              {lang === 'en'
                ? 'No documents required for this season. Your coach will reach out if anything is needed.'
                : 'No se requieren documentos para esta temporada. Tu entrenador te contactará si se necesita algo.'}
            </p>
          </div>
        ) : (
          requiredDocs.map(docType => {
            const config = DOC_TYPES[docType] ?? {
              label:   docType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              labelEs: docType.replace(/_/g, ' '),
              accept:  'image/*,application/pdf',
              icon:    '📄',
              hint:    'Upload the required document — JPG, PNG, or PDF',
              hintEs:  'Sube el documento requerido — JPG, PNG o PDF',
            };
            const upload = uploads[docType] || { status: 'idle' };
            return (
              <DocUploadCard
                key={docType}
                docType={docType}
                config={config}
                upload={upload}
                lang={lang}
                onFileSelect={handleFileSelect}
              />
            );
          })
        )}

        {/* All done celebration */}
        {allDone && (
          <div className="bg-green-600 rounded-2xl p-6 text-white text-center">
            <div className="text-4xl mb-3">🎉</div>
            <h2 className="text-xl font-black">
              {lang === 'en' ? "All documents submitted!" : "¡Todos los documentos enviados!"}
            </h2>
            <p className="text-green-200 text-sm mt-2">
              {lang === 'en'
                ? 'Your coach has been notified. You\'re fully registered!'
                : 'Tu entrenador fue notificado. ¡Estás completamente registrado!'}
            </p>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 pb-4">
          {lang === 'en'
            ? `Files are stored securely and only accessible to ${team?.name ?? 'your team'}.`
            : `Los archivos se almacenan de forma segura y solo son accesibles para ${team?.name ?? 'tu equipo'}.`}
        </p>
      </div>
    </div>
  );
}

/* ── Doc Upload Card ─────────────────────────────────── */
function DocUploadCard({ docType, config, upload, lang, onFileSelect }) {
  const inputRef = useRef(null);
  const isDone      = upload.status === 'done';
  const isUploading = upload.status === 'uploading';
  const isError     = upload.status === 'error';
  const label       = lang === 'es' ? config.labelEs : config.label;
  const hint        = lang === 'es' ? config.hintEs  : config.hint;

  return (
    <div className={clsx(
      'bg-white rounded-2xl border-2 shadow-sm overflow-hidden transition-all',
      isDone ? 'border-green-400' : isError ? 'border-red-300' : 'border-slate-100'
    )}>
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <span className="text-xl">{config.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-slate-800">{label}</p>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{hint}</p>
        </div>
        {isDone && (
          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
        )}
        {isError && (
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
        )}
      </div>

      <div className="px-5 py-4">
        {isDone ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
              <CheckCircleIcon className="h-4 w-4" />
              {upload.fileName || (lang === 'en' ? 'Uploaded' : 'Subido')}
            </div>
            <button
              onClick={() => inputRef.current?.click()}
              className="text-xs text-slate-400 hover:text-blue-500 underline transition-colors"
            >
              {lang === 'en' ? 'Replace' : 'Reemplazar'}
            </button>
          </div>
        ) : isUploading ? (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-slate-500">
              <span>{lang === 'en' ? 'Uploading…' : 'Subiendo…'}</span>
              <span>{upload.progress}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${upload.progress}%` }} />
            </div>
          </div>
        ) : (
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all text-sm font-medium"
          >
            {config.accept?.includes('image') ? (
              <PhotoIcon className="h-5 w-5" />
            ) : (
              <DocumentArrowUpIcon className="h-5 w-5" />
            )}
            {lang === 'en' ? 'Tap to upload or take photo' : 'Toca para subir o tomar foto'}
          </button>
        )}

        {isError && (
          <p className="text-xs text-red-500 mt-2">
            {lang === 'en' ? 'Upload failed. Please try again.' : 'Error al subir. Intenta de nuevo.'}
          </p>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={config.accept}
          capture={config.capture}
          className="hidden"
          onChange={e => e.target.files?.[0] && onFileSelect(docType, e.target.files[0])}
        />
      </div>
    </div>
  );
}
